import {
    BrandingFooter,
    BrandingHeader
} from '@models/branding';
import {
    Constants,
    ConstantsMap
} from '@models/Constants';
import axios from 'axios';

export interface UploadDocumentsConfig {
    uploadFileSizeLimitMB: number;
    acceptedFileTypes: string;
    acceptableDocumentsUrl: string;
    eVerifyDocsOnly: boolean;
    eVerifyAllowedDocs: string[];
}

export interface IConfigurationValuesService {
    readonly applicationName: string;
    readonly piwikUrl: string;
    readonly piwikTrackingId: number;
    readonly version: string;
    readonly apiUrl: string;
    readonly preferredLocale: string;
    readonly uscisWebsiteUrl: string;
    readonly uscisSection3FormI9Url: string;
    readonly uscisSection2FormI9Url: string;
    readonly uscisSection2FormI9UrlText: string;
    readonly showExtraUploadInstructions: boolean;
    readonly personPidKey: string;
    readonly i9ListADocuments: string[];
    readonly i9ListBDocuments: string[];
    readonly i9ListCDocuments: string[];
    readonly personPidIvLength: number;
    readonly prepopulateI9DocumentDetails: boolean;
    readonly brandingFooter: BrandingFooter;
    readonly faqUrl: string;
    readonly supportUrl: string;
    readonly privacyPolicyUrl: string;
    readonly partnerId: string;
    readonly brandingHeader: BrandingHeader;
    readonly hideDocumentsPhysicallyExamined: boolean;
    readonly enabledUploadDocuments: boolean;
    readonly uploadDocuments: UploadDocumentsConfig;
    readonly displayInvitationLink: boolean;
    readonly invitationURL: string;
    readonly defaultTodaysDateToToday: boolean;
    readonly showSection1: boolean;
    readonly enableLanguageES: boolean;
    readonly expirationDateForAllListA: boolean;
    readonly bannerHeaderUrl: string;
    readonly contactLinkFooterUrl: string;
    readonly sentryDsnUrl: string;
    readonly tracesSampleRate: number;
    readonly sentryEnvironment: string;
    initAsync(): Promise<boolean>;
}

export default class ConfigurationValuesService implements IConfigurationValuesService {
    static serviceInstance: IConfigurationValuesService | undefined;
    private values: ConstantsMap;

    static getInstance() {
        if (this.serviceInstance === undefined) {
            this.serviceInstance = new ConfigurationValuesService();
        }
        return this.serviceInstance;
    }

    static removeInstance() {
        this.serviceInstance = undefined;
    }

    constructor() {
        this.values = {} as ConstantsMap;
    }

    private useConstants(constants: any) {
        this.values = constants;
    }

    async initAsync(): Promise<boolean> {
        try {
            const response = await axios.get('static/data/static-configuration.json', {});
            this.useConstants(response.data.constants);
            return true;
        } catch (e) {
            return false;
        }
    }

    get i9ListADocuments(): string[] {
        return this.values[Constants.i9ListADocuments];
    }

    get i9ListBDocuments(): string[] {
        return this.values[Constants.i9ListBDocuments];
    }

    get i9ListCDocuments(): string[] {
        return this.values[Constants.i9ListCDocuments];
    }

    get applicationName(): string {
        return this.values[Constants.applicationName];
    }

    get piwikUrl(): string {
        return this.values[Constants.piwikUrl];
    }

    get piwikTrackingId(): number {
        return this.values[Constants.piwikTrackingId];
    }

    get version(): string {
        return this.values[Constants.version];
    }

    get apiUrl(): string {
        return this.values[Constants.apiUrl];
    }

    get preferredLocale(): string {
        return this.values[Constants.preferredLocale];
    }

    get uscisWebsiteUrl(): string {
        return this.values[Constants.uscisWebsiteUrl];
    }

    get uscisSection3FormI9Url(): string {
        return this.values[Constants.uscisSection3FormI9Url];
    }

    get uscisSection2FormI9Url(): string {
        return this.values[Constants.uscisSection2FormI9Url];
    }

    get uscisSection2FormI9UrlText(): string {
        return this.values[Constants.uscisSection2FormI9UrlText];
    }

    get showExtraUploadInstructions(): boolean {
        return this.values[Constants.showExtraUploadInstructions];
    }

    get personPidKey(): string {
        return this.values[Constants.personPidKey];
    }

    get personPidIvLength(): number {
        return this.values[Constants.personPidIvLength];
    }

    get prepopulateI9DocumentDetails(): boolean {
        return this.values[Constants.prepopulateI9DocumentDetails];
    }

    get brandingFooter(): BrandingFooter {
        return this.values[Constants.brandingFooter];
    }

    get faqUrl(): string {
        return this.values[Constants.faqUrl];
    }

    get supportUrl(): string {
        return this.values[Constants.supportUrl];
    }

    get privacyPolicyUrl(): string {
        return this.values[Constants.privacyPolicyUrl];
    }

    get partnerId(): string {
        return this.values[Constants.partnerId];
    }

    get hideDocumentsPhysicallyExamined(): boolean {
        return this.values[Constants.hideDocumentsPhysicallyExamined];
    }

    get brandingHeader(): BrandingHeader {
        return this.values[Constants.brandingHeader];
    }

    get enabledUploadDocuments(): boolean {
        return this.values?.[Constants.enabledUploadDocuments];
    }

    get uploadDocuments(): UploadDocumentsConfig {
        return this.values?.[Constants.uploadDocuments];
    }

    get displayInvitationLink(): boolean {
        return this.values?.[Constants.displayInvitationLink];
    }

    get invitationURL(): string {
        return this.values?.[Constants.invitationURL];
    }

    get defaultTodaysDateToToday(): boolean {
        return this.values?.[Constants.defaultTodaysDateToToday];
    }

    get showSection1(): boolean {
        return this.values?.[Constants.showSection1];
    }

    get enableLanguageES(): boolean {
        return this.values?.[Constants.enableLanguageES];
    }

    get expirationDateForAllListA(): boolean {
        return this.values?.[Constants.expirationDateForAllListA];
    }

    get bannerHeaderUrl(): string {
        return this.values?.[Constants.bannerHeaderUrl]
    }

    get contactLinkFooterUrl(): string {
        return this.values?.[Constants.contactLinkFooterUrl]
    }

    get sentryDsnUrl(): string {
        return this.values?.[Constants.sentryDsnUrl];
    }

    get tracesSampleRate(): number {
        return this.values?.[Constants.tracesSampleRate];
    }

    get sentryEnvironment(): string {
        return this.values?.[Constants.sentryEnvironment];
    }
}



// WEBPACK FOOTER //
// ./src/services/ConfigurationValuesService.ts